import {inject, injectable, decorate} from 'inversify';
import {from, of} from 'rxjs';
import {fromPromise} from 'rxjs/internal-compatibility';
import {debounce, filter, map, pairwise, startWith, switchMap, tap} from 'rxjs/operators';
import {WixMusicWidgetModule} from '../../../../../typings/wix-music-widget';
import {services} from '../../../../ioc/services';
import {appLoadingPhaseStart, LoadingPhases} from '../../../../logging/fedops';
import {logError} from '../../../../logging/logging';
import {ISiteStorage} from '../../../../services/SiteStorage/ISiteStorage';
import {debug, debugScoped} from '../../../../utils/debug';
import {layoutValues} from '../../../layout-values';
import {resizeOnLayoutChange, resizeOnReInitNew} from '../../../widget-resizer';
import {createPlayer} from '../../createPlayer/createPlayer';
import {getWindowObservables} from '../../getWindowObservables';
import {migrationEnd$, startMigrationTrack} from '../../migrationObserver/migrationObserver';
import {subscribeToModal} from '../../modals';
import {playPauseObserver} from '../../playPauseObserver';
import {scrollToPlayer} from '../../scrollToPlayer';
import {debounceWithoutComplete} from '../../utils/debounceWithoutComplete';
import {subscribe} from '../../utils/subscribe';
import {viewMode$, ViewModes} from '../../viewMode';
import {documentVisible, onPlayerRendered} from '../../visibilityObserver';
import {ICollectionObservable} from '../CollectionObservable/ICollectionObservable';
import {ISettingsProvider} from '../SettingsProvider/ISettingsProvider';
import {IWidgetPlayer} from './IWidgetPlayer';
import {start as startDebugPanel} from '../../../../utils/debugPanel';

export class WidgetPlayer implements IWidgetPlayer {
  constructor(
    private readonly collectionObservable: ICollectionObservable,
    private readonly settingsProvider: ISettingsProvider,
    private readonly siteStorage: ISiteStorage,
  ) {}

  init(PlPromise: Promise<WixMusicWidgetModule>) {
    const debounceMigration = startMigrationTrack();
    const element = document.querySelector('wix-pl-app, .wix-pl-app, #WixMusicPlayerWidget');
    if (!element) {
      throw new Error('Cannot find place where to render player');
    }
    const Pl$ = fromPromise(PlPromise);
    subscribe(Pl$, {
      next: (PL: WixMusicWidgetModule) => {
        PL.errorReporter.on('error', logError);
        PL.appStatus.on('step', name => {
          switch (name) {
            case 'styles-added':
              appLoadingPhaseStart(LoadingPhases.StylesAdded);
              return;
            case 'app-mounted':
              appLoadingPhaseStart(LoadingPhases.AppMounted);
              return;
          }
        });
      },
    });
    const setting$ = this.settingsProvider.getSettings().pipe(debounceMigration());
    appLoadingPhaseStart(LoadingPhases.DataRetrieval);

    const collection$ = this.collectionObservable.getCollection();

    const player$ = createPlayer({element, Pl$, setting$, collection$});

    const resize$ = viewMode$.pipe(
      filter(viewMode => viewMode === ViewModes.Editor),
      debounce(() => documentVisible()),
      switchMap(() => setting$.pipe(startWith(null), pairwise())),
      switchMap(([prevSettings, currSettings]) =>
        player$.pipe(map(player => [player, prevSettings, currSettings] as const)),
      ),
      switchMap(([player, prevSettingsOrNull, settings]) =>
        of(1).pipe(
          debug('resize', 'new settings'),
          debounceMigration(),
          debounceWithoutComplete(() => onPlayerRendered(player)),
          debounceWithoutComplete(() => documentVisible()),
          switchMap(() => from(this.siteStorage.get<number>('numberTracksShown'))),
          tap(numberTracksShown => {
            if (!settings) {
              throw new Error('Something wen wrong. Settings should be defined');
            }
            if (prevSettingsOrNull && prevSettingsOrNull.numbers.layout !== settings.numbers.layout) {
              debugScoped('resize', 'starting layout adjust');
              resizeOnLayoutChange(settings, player);
            } else {
              debugScoped('resize', 'starting resize');
              resizeOnReInitNew(settings, player, numberTracksShown);
            }
            const rect = settings.rect;
            if (!rect) {
              console.error('No rect in editor');
              return;
            }
            layoutValues[settings.numbers.layout || 0].width = rect.width;
          }),
        ),
      ),
    );

    const playPause$ = playPauseObserver(player$);
    subscribe(resize$);
    subscribe(playPause$);
    subscribe(
      player$.pipe(
        tap(() => {
          startDebugPanel().catch(error => {
            console.error('could not start debug panel', error);
          });
        }),
      ),
    );
    subscribe(subscribeToModal(player$));
    subscribe(migrationEnd$, {
      next: () => {
        console.info('Reloading widget after migration finished');
        location.reload();
      },
    });
    subscribe(getWindowObservables(player$));
    subscribe(scrollToPlayer(setting$));
  }
}

decorate(injectable(), WidgetPlayer);
decorate(inject(services.collectionObservable) as ParameterDecorator, WidgetPlayer, 0);
decorate(inject(services.settingsProvider) as ParameterDecorator, WidgetPlayer, 1);
decorate(inject(services.siteStorage) as ParameterDecorator, WidgetPlayer, 2);
